import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { api } from '../../api';
import { useAuth } from '../../AuthContext';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Checkbox } from '../../components/ui/checkbox';
import { Badge } from '../../components/ui/badge';
import { CheckCircle2, FileSignature, ShieldCheck, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const CONTRACT_LABELS = {
  BOARDING: 'Boarding Services Agreement',
  BOARD_AND_TRAIN: 'Board & Train Services Agreement',
};

const SignaturePad = ({ value, onChange }) => {
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const lastPoint = useRef(null);

  useEffect(() => {
    const c = canvasRef.current;
    if (!c) return;
    const ctx = c.getContext('2d');
    ctx.fillStyle = '#FFFFFF';
    ctx.fillRect(0, 0, c.width, c.height);
    ctx.strokeStyle = '#0B1220';
    ctx.lineWidth = 2.5;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
  }, []);

  const pos = (e) => {
    const c = canvasRef.current;
    const rect = c.getBoundingClientRect();
    const t = e.touches ? e.touches[0] : e;
    return { x: (t.clientX - rect.left) * (c.width / rect.width), y: (t.clientY - rect.top) * (c.height / rect.height) };
  };
  const start = (e) => { e.preventDefault(); drawing.current = true; lastPoint.current = pos(e); };
  const move = (e) => {
    if (!drawing.current) return; e.preventDefault();
    const p = pos(e); const ctx = canvasRef.current.getContext('2d');
    ctx.beginPath(); ctx.moveTo(lastPoint.current.x, lastPoint.current.y); ctx.lineTo(p.x, p.y); ctx.stroke();
    lastPoint.current = p;
  };
  const end = () => {
    if (!drawing.current) return; drawing.current = false; lastPoint.current = null;
    onChange?.(canvasRef.current.toDataURL('image/png'));
  };
  const clear = () => {
    const c = canvasRef.current; const ctx = c.getContext('2d');
    ctx.fillStyle = '#FFFFFF'; ctx.fillRect(0, 0, c.width, c.height);
    onChange?.('');
  };

  return (
    <div className='mt-2'>
      <div className='relative border-2 border-dashed border-slate-300 dark:border-slate-700 rounded-md bg-white'>
        <canvas
          ref={canvasRef}
          width={640} height={180}
          className='w-full h-[180px] touch-none rounded-md'
          onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
          onTouchStart={start} onTouchMove={move} onTouchEnd={end}
          data-testid='signature-canvas'
        />
        {!value && (
          <div className='absolute inset-0 flex items-center justify-center pointer-events-none text-slate-300 text-sm italic'>
            Sign here with your finger or mouse
          </div>
        )}
      </div>
      <button type='button' onClick={clear} className='mt-1 text-[11px] text-slate-500 hover:text-[#B42318] underline' data-testid='signature-clear'>
        Clear signature
      </button>
    </div>
  );
};

const SignContractDialog = ({ open, onClose, contractType, onSigned }) => {
  const { user } = useAuth();
  const [tpl, setTpl] = useState(null);
  const [signer, setSigner] = useState('');
  const [typed, setTyped] = useState('');
  const [signatureImg, setSignatureImg] = useState('');
  const [dogName, setDogName] = useState('');
  const [agree, setAgree] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open || !contractType) return;
    setSigner(user?.full_name || '');
    setTyped('');
    setSignatureImg('');
    setAgree(false);
    api.get(`/contracts/templates/${contractType}`).then(r => setTpl(r.data));
  }, [open, contractType, user]);

  if (!open) return null;

  const submit = async () => {
    if (!agree) { toast.error('You must agree to the terms'); return; }
    if (!signer.trim() || !typed.trim()) { toast.error('Full name and typed signature required'); return; }
    setSubmitting(true);
    try {
      const { data } = await api.post('/contracts/sign', {
        contract_type: contractType,
        signer_full_name: signer,
        typed_signature: typed,
        signature_image_base64: signatureImg || null,
        dog_name: dogName || null,
        agreed_to_terms: true,
      });
      toast.success('Contract signed and stored');
      onSigned?.(data);
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not sign');
    } finally { setSubmitting(false); }
  };

  return (
    <div className='fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-3 overflow-y-auto' data-testid='sign-contract-dialog'>
      <Card className='w-full max-w-3xl my-6 bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-700'>
        <div className='p-5 border-b border-slate-200 dark:border-slate-700 flex items-center justify-between'>
          <div className='font-display font-bold text-lg text-slate-900 dark:text-slate-50 flex items-center gap-2'>
            <FileSignature className='w-5 h-5 text-[#1B3A87] dark:text-[#93C5FD]' /> {tpl?.title || CONTRACT_LABELS[contractType] || 'Contract'}
          </div>
          <button onClick={onClose} className='text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 text-2xl leading-none'>×</button>
        </div>
        <div className='p-5 max-h-[55vh] overflow-y-auto whitespace-pre-wrap text-sm text-slate-700 dark:text-slate-200 leading-relaxed' data-testid='contract-body'>
          {tpl ? tpl.body : <Loader2 className='w-4 h-4 animate-spin' />}
        </div>
        <div className='p-5 border-t border-slate-200 dark:border-slate-700 space-y-3 bg-slate-50 dark:bg-slate-950'>
          <div className='grid sm:grid-cols-2 gap-3'>
            <div>
              <label className='text-xs font-medium text-slate-700 dark:text-slate-200'>Full legal name *</label>
              <Input value={signer} onChange={(e) => setSigner(e.target.value)} className='h-11 mt-1' data-testid='contract-signer-name' />
            </div>
            <div>
              <label className='text-xs font-medium text-slate-700 dark:text-slate-200'>Dog name (optional)</label>
              <Input value={dogName} onChange={(e) => setDogName(e.target.value)} className='h-11 mt-1' data-testid='contract-dog-name' />
            </div>
          </div>
          <div>
            <label className='text-xs font-medium text-slate-700 dark:text-slate-200'>Type your full name as your signature *</label>
            <Input value={typed} onChange={(e) => setTyped(e.target.value)} placeholder='Your typed signature carries legal weight under the E-SIGN Act' className='h-11 mt-1 font-mono italic' data-testid='contract-typed-signature' />
          </div>
          <div>
            <label className='text-xs font-medium text-slate-700 dark:text-slate-200'>Drawn signature (optional, recommended)</label>
            <SignaturePad value={signatureImg} onChange={setSignatureImg} />
          </div>
          <label className='flex items-start gap-2 text-xs text-slate-700 dark:text-slate-200 cursor-pointer'>
            <Checkbox checked={agree} onCheckedChange={(v) => setAgree(!!v)} className='mt-0.5' data-testid='contract-agree-checkbox' />
            <span>
              I have read and agree to this Agreement. By clicking <strong>I AGREE</strong> I am providing my electronic signature
              and intend to be legally bound by this Agreement pursuant to the U.S. E-SIGN Act. My IP address, timestamp, and
              user agent will be recorded as part of this signature.
            </span>
          </label>
          <div className='flex flex-wrap gap-2 justify-end pt-2'>
            <Button variant='outline' onClick={onClose} data-testid='contract-cancel'>Cancel</Button>
            <Button disabled={!agree || submitting} onClick={submit} className='bg-[#C8102E] hover:bg-[#A50D26] text-white' data-testid='contract-submit'>
              {submitting ? <Loader2 className='w-4 h-4 animate-spin mr-2' /> : <CheckCircle2 className='w-4 h-4 mr-2' />}
              {submitting ? 'Signing…' : 'I AGREE · Sign electronically'}
            </Button>
          </div>
        </div>
      </Card>
    </div>
  );
};

export default function Contracts() {
  const [contracts, setContracts] = useState([]);
  const [openType, setOpenType] = useState(null);
  const [searchParams] = useSearchParams();
  const load = () => api.get('/contracts/mine').then(r => setContracts(r.data));
  useEffect(() => { load(); }, []);
  useEffect(() => {
    const open = searchParams.get('open');
    if (open && (open === 'BOARDING' || open === 'BOARD_AND_TRAIN')) setOpenType(open);
  }, [searchParams]);

  const signed = (t) => contracts.find(c => c.contract_type === t);

  return (
    <div className='p-4 sm:p-6 lg:p-8 max-w-5xl mx-auto'>
      <div className='mb-6'>
        <div className='text-[10px] uppercase tracking-[0.16em] font-bold text-[#C8102E]'>E-Sign · Legally binding</div>
        <h1 className='font-display text-2xl sm:text-3xl font-bold text-slate-900 dark:text-slate-50 mt-1'>My Contracts</h1>
        <p className='text-sm text-slate-500 dark:text-slate-400 mt-1'>
          Sign your boarding and Board & Train agreements digitally. Each signature is timestamped with your IP and stored securely.
        </p>
      </div>

      <div className='grid md:grid-cols-2 gap-4'>
        {['BOARDING', 'BOARD_AND_TRAIN'].map((t) => {
          const c = signed(t);
          return (
            <Card key={t} className='p-5 border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900' data-testid={`contract-card-${t}`}>
              <div className='flex items-start justify-between gap-3'>
                <div>
                  <div className='text-[10px] uppercase tracking-[0.16em] font-bold text-[#1B3A87] dark:text-[#93C5FD]'>{t === 'BOARDING' ? 'Boarding' : 'Board & Train'}</div>
                  <div className='font-display font-bold text-lg text-slate-900 dark:text-slate-50 mt-1'>{CONTRACT_LABELS[t]}</div>
                </div>
                <ShieldCheck className='w-6 h-6 text-[#167A5A]' />
              </div>
              {c ? (
                <div className='mt-4 space-y-1 text-xs text-slate-600 dark:text-slate-300'>
                  <div><Badge className='bg-[#167A5A]/15 text-[#167A5A] dark:text-[#7DD3A8] border-0'><CheckCircle2 className='w-3 h-3 mr-1' /> Signed</Badge></div>
                  <div>Signed by <strong>{c.signer_full_name}</strong></div>
                  <div>{new Date(c.signed_at).toLocaleString()}</div>
                  <div className='font-mono text-[10px] truncate'>hash: {c.document_hash.slice(0, 24)}…</div>
                  <Button variant='outline' size='sm' className='mt-2' onClick={() => setOpenType(t)} data-testid={`contract-resign-${t}`}>Re-sign updated version</Button>
                </div>
              ) : (
                <Button className='w-full mt-4 bg-[#1B3A87] hover:bg-[#16306F] text-white' onClick={() => setOpenType(t)} data-testid={`contract-sign-${t}`}>
                  <FileSignature className='w-4 h-4 mr-2' /> Read & Sign
                </Button>
              )}
            </Card>
          );
        })}
      </div>

      {contracts.length > 0 && (
        <Card className='mt-6 p-5 border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900'>
          <h2 className='font-display font-bold text-lg text-slate-900 dark:text-slate-50 mb-3'>Signing History</h2>
          <div className='space-y-2'>
            {contracts.map((c) => (
              <div key={c.id} className='flex items-center justify-between p-3 rounded-md bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-sm' data-testid={`contract-history-${c.id}`}>
                <div>
                  <div className='font-semibold text-slate-900 dark:text-slate-50'>{c.contract_title}</div>
                  <div className='text-xs text-slate-500 dark:text-slate-400'>{new Date(c.signed_at).toLocaleString()} · IP {c.ip_address || '?'}</div>
                </div>
                <Badge className='bg-[#167A5A]/15 text-[#167A5A] dark:text-[#7DD3A8] border-0 font-mono text-[10px]'>{c.document_hash.slice(0, 8)}…</Badge>
              </div>
            ))}
          </div>
        </Card>
      )}

      <SignContractDialog
        open={!!openType}
        contractType={openType}
        onClose={() => setOpenType(null)}
        onSigned={() => load()}
      />
    </div>
  );
}
